import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import { formatDate } from 'util/formatters/dateFormatter';
import { locale } from '@tcc/i18n';
import { getMessage } from 'util/locale';
import { convertFertilizerSummaryForLocale, parseDecimals} from 'util/unitConversion';

import { Label } from '@tcc/crate';

import HtmlText from 'html-text/HtmlText';
import ValueWithHeader from 'common/ValueWithHeader';
import { protectionApplicationTypes } from 'constants/activities';

const baseString = 'vra-product-summary';
const classString = classNames({
  [`${baseString}`]: true,
  'vra-application-summary': true
});
const ApplicationSummary = ({ activity, activityCategory, summary }) => {
  const applicationStats = summary.map((applicationSummary, index) => {
    const { product, chemicalMix } = applicationSummary;
    const { convertedFertilizerRate, convertedTotalProduct } = convertFertilizerSummaryForLocale(applicationSummary);
    const date = activity.date.length ? activity.date.substring(0, 10) : activity.date;
    const formattedPlannedDate = formatDate(date, { locale });
    const activityCategorySlug = activityCategory.activityCategoryUuid;

    const isTankMix = activity.type === protectionApplicationTypes.APPLICATION_MIX;
    const tankMixProducts = !isNil(chemicalMix) ? chemicalMix : [product];

    const getProductName = (product) => {
      return product ? product.name : '';
    };

    return (
      <div className={`${baseString}__list`} key={`protection-summary-application-${index}`}>
        
        <div
          className={`${baseString}__list__item`}
          data-test-id={'protection-summary-product'}
          key={`protection-summary-product-${index}`}
        >
          <ValueWithHeader
            header={isTankMix 
              ? getMessage('label-text__mix--product-count', { productCount: tankMixProducts.length}) 
              : getMessage(`label-text__product-${activityCategorySlug}`)} // TODO: CFVWEB-8880 update CSS text-overflow rule
            nestedBadge={product && product.isCustom ? <Label
              className='custom-chemical-nested-badge'
              status='default'
              testId='crate-label-success-primary'>
              {getMessage('text__use-custom-label-abbrev')}
            </Label> : undefined}
            testId={'protection-summary-chemical-summary'}
            value={isTankMix ? getMessage('label-text__mix') : getProductName(product) }
          />
        </div>
        <div
          className={`${baseString}__list__item`}
          data-test-id={'protection-summary-average-rate'}
          key={`protection-summary-avg-rate-${index}`}
        >
          <ValueWithHeader
            // using the base string from fertility to keep translations synced 
            header={getMessage('title-text__protection__avg-fertilizer-rate')}
            testId={'script-protection-average-rate'}
            value={
              <HtmlText
                messageKey={'text__value-with-unit'}
                messageParams={{
                  value: parseDecimals(convertedFertilizerRate.scalarLocaleString),
                  unit: convertedFertilizerRate.symbol.quantitySpecific
                }}
              />
            }
          />
        </div>
        <div
          className={`${baseString}__list__item`}
          data-test-id={'protection-timing'}
          key={`protection-summary-product-${index}`}
        >
          <ValueWithHeader
            header={getMessage('text__script-activity-date')}
            testId={'script-protection-date'}
            value={formattedPlannedDate}
          />
        </div>
        <div
          className={`${baseString}__list__item`}
          data-test-id={'protection-summary-total-product'}
          key={`protection-summary-num-application-bags-${index}`}
        >
          <ValueWithHeader
            header={getMessage('title-text__protection__total-product')}
            testId={'script-protection-total-rate'}
            value={
              <HtmlText
                messageKey={'text__value-with-unit'}
                messageParams={{
                  value: parseDecimals(convertedTotalProduct.scalarLocaleString),
                  unit: convertedTotalProduct.symbol.quantitySpecific
                }}
              />
            }
          />
        </div>
      </div>
    );
  });

  return <section className={classString}>{applicationStats}</section>;
};
ApplicationSummary.propTypes = {
  activity: PropTypes.object,
  activityCategory: PropTypes.object.isRequired,
  summary: PropTypes.array.isRequired
};
export default ApplicationSummary;
